        import React, { createContext, useContext, useEffect, useState } from 'react';
import appConfig from '../config/appConfig';
import { normalizeClinicId } from '../utils/clinicIdUtils';

const isLocalEnvironment = () => {
    if (typeof window === 'undefined') return false;
    return Boolean(window.location.hostname);
};

const AuthContext = createContext(null);

const buildSessionUrl = (base) => {
  const normalized = (base || '').replace(/\/+$/, '');
  if (!normalized) {
    return '/api/clinic/auth/session';
  }
  if (normalized.match(/\/api\/clinic\/auth$/i)) {
    return `${normalized}/session`;
  }
  if (normalized.match(/\/api$/i)) {
    return `${normalized}/clinic/auth/session`;
  }
  return `${normalized}/api/clinic/auth/session`;
};

export function AuthProvider({ children }) {
    const [auth, setAuth] = useState({ loading: true, authenticated: false, clinic: null });

    useEffect(() => {
        async function fetchSession() {
                const API_BASE = appConfig.apiBase || process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_URL || '';
                const url = buildSessionUrl(API_BASE);
                try {
                    const res = await fetch(url, { credentials: 'include' });
                    if (res.ok) {
                        const data = await res.json();
                        const normalizedClinicId = normalizeClinicId(data?.clinicId);
                        if (data?.authenticated && normalizedClinicId) {
                            setAuth({
                                loading: false,
                                authenticated: true,
                                clinic: {
                                    id: normalizedClinicId,
                                    code: data.clinicCode,
                                    name: data.clinicName,
                                },
                            });
                            return;
                        }
                    }
                    setAuth({ loading: false, authenticated: false, clinic: null });
                } catch (e) {
                    console.error('Session fetch failed', e);
                    setAuth({ loading: false, authenticated: false, clinic: null });
                }
            }

        fetchSession();
    }, []);

    useEffect(() => {
        // expose clinic info to non-React modules (services) during migration
        try {
            window.__CLINIC_ID__ = auth?.clinic?.id ?? null;
            window.__CLINIC_NAME__ = auth?.clinic?.name ?? null;
            window.__CLINIC_CODE__ = auth?.clinic?.code ?? null;
        } catch (e) {
            // ignore in non-browser environments
        }
    }, [auth]);

    const refresh = async () => {
        setAuth(prev => ({ ...prev, loading: true }));
        const API_BASE = appConfig.apiBase || process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_URL || '';
        const url = buildSessionUrl(API_BASE);
        try {
            const res = await fetch(url, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                const normalizedClinicId = normalizeClinicId(data?.clinicId);
                if (data?.authenticated && normalizedClinicId) {
                    setAuth({
                        loading: false,
                        authenticated: true,
                        clinic: {
                            id: normalizedClinicId,
                            code: data.clinicCode,
                            name: data.clinicName,
                        },
                    });
                    return true;
                }
            }
        } catch (e) {
            console.error(e);
        }
        setAuth({ loading: false, authenticated: false, clinic: null });
        return false;
    };

    return (
        <AuthContext.Provider value={{ auth, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
